import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { Flame, ChevronRight, Award, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const DAY = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "narrow" });
const MILESTONES = [
  [3, "Spark", "bg-amber-100 text-amber-800 border-amber-300"],
  [7, "One week", "bg-orange-100 text-orange-800 border-orange-300"],
  [14, "Fortnight", "bg-rose-100 text-rose-800 border-rose-300"],
  [30, "One month", "bg-violet-100 text-violet-800 border-violet-300"],
  [60, "Two months", "bg-sky-100 text-sky-800 border-sky-300"],
  [100, "Century", "bg-yellow-200 text-yellow-900 border-yellow-500"],
];

const pop = () => {
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const colors = ["#D9AE34", "#C8102E", "#0E1A38", "#F97316"];
  confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors, zIndex: 9999 });
  setTimeout(() => confetti({ particleCount: 80, spread: 110, origin: { y: 0.5 }, colors, zIndex: 9999 }), 350);
};

const Celebrate = ({ m, onClose }) => {
  const meta = MILESTONES.find(([n]) => n === m);
  useEffect(() => { if (m) pop(); }, [m]);
  return (
    <Dialog open={Boolean(m)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="milestone-dialog" className="max-w-sm rounded-3xl bg-white text-center">
        <DialogHeader>
          <div className="mx-auto mb-2 grid h-16 w-16 place-items-center rounded-full bg-orange-100 text-orange-600"><Flame className="h-9 w-9" /></div>
          <DialogTitle data-testid="milestone-title" className="text-center font-display text-3xl text-slate-900">{m}-day streak!</DialogTitle>
          <DialogDescription className="text-center text-slate-700">You've logged your day {m} days in a row and earned the <b>{meta?.[1]}</b> badge. It's on your profile in Settings.</DialogDescription>
        </DialogHeader>
        <button data-testid="milestone-close" onClick={onClose} className="mt-2 rounded-full bg-gold-400 px-5 py-3 text-sm font-bold text-black hover:bg-gold-300">Keep it going</button>
      </DialogContent>
    </Dialog>
  );
};

export const MilestonesCard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/daylog/streak").then((r) => setS(r.data ?? null)).catch(() => {}); }, []);
  if (!s) return null;
  const earned = Array.isArray(s.milestones) ? s.milestones : [];
  return (
    <section data-testid="milestones-card" className="card p-5 sm:p-6">
      <p className="eyebrow flex items-center gap-2"><Award className="w-4 h-4" />Streak badges</p>
      <p className="mt-1 text-sm text-slate-600">Current streak {s.streak ?? 0} {s.streak === 1 ? "day" : "days"} · best {s.best ?? 0}{s.next_milestone ? ` · next badge at ${s.next_milestone}` : ""}</p>
      <ul className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
        {MILESTONES.map(([n, name, cls]) => {
          const on = earned.includes(n);
          return (
            <li key={n} data-testid={`milestone-badge-${n}`} data-earned={on} className={`flex flex-col items-center rounded-2xl border p-3 text-center ${on ? cls : "border-slate-200 bg-slate-50 text-slate-500"}`}>
              {on ? <Flame className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
              <b className="mt-1 font-display text-xl leading-none">{n}</b>
              <span className="mt-0.5 text-xs font-semibold">{name}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export const StreakBadge = () => {
  const [s, setS] = useState(null);
  const [party, setParty] = useState(null);
  useEffect(() => { api.get("/daylog/streak").then((r) => { setS(r.data ?? null); if (r.data?.celebrate) setParty(r.data.celebrate); }).catch(() => {}); }, []);
  const closeParty = () => { setParty(null); api.post("/daylog/milestones/seen").catch(() => {}); };
  if (!s) return null;
  const n = s.streak ?? 0;
  const last7 = Array.isArray(s.last7) ? s.last7 : [];
  const hint = s.today_logged ? "Logged today. Keep it going tomorrow!" : n > 0 ? "Log today to keep your streak alive" : "Log what you did today to start a streak";
  return (
    <>
    <Celebrate m={party} onClose={closeParty} />
    <Link to="/daylog" data-testid="streak-badge" className="card card-hover flex items-center gap-4 p-4 sm:p-5">
      <span className={`grid place-items-center w-12 h-12 shrink-0 rounded-2xl ${n > 0 ? "bg-orange-100 text-orange-600" : "bg-slate-100 text-slate-500"}`}><Flame className="w-6 h-6" /></span>
      <span className="flex-1 min-w-0">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <b data-testid="streak-count" className="font-display text-2xl text-slate-900">{n}-day streak</b>
          {s.best > 0 && <span data-testid="streak-best" className="text-xs text-slate-600">Best {s.best}</span>}
        </span>
        <span data-testid="streak-hint" className="block text-xs text-slate-600">{hint}</span>
        <span className="mt-2 flex gap-1.5">
          {last7.map((d) => (
            <span key={d.date} data-testid={`streak-day-${d.date}`} title={d.date} className={`grid place-items-center w-6 h-6 rounded-full text-[10px] font-bold ${d.logged ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"}`}>{DAY(d.date)}</span>
          ))}
        </span>
      </span>
      <ChevronRight className="w-5 h-5 text-slate-500" />
    </Link>
    </>
  );
};
